import {
  ETokenUsageBreakdownDimension,
  ETokenUsageTrendGranularity,
  ETokenUsageTrendMetric,
} from "../types/token-usage";
import { ETokenCostPeriod } from "../types/token-usage-cost";

// NOTE: Order drives the period picker on every client. `as const` keeps
// labelKey as literal i18n key types so the typed t() accepts them. labelKey
// resolves against the "settings" namespace (tokenCostUsage.* keys).
export const TOKEN_COST_PERIOD_OPTIONS = [
  {
    value: ETokenCostPeriod.LAST_7_DAYS,
    labelKey: "tokenCostUsage.period.last7Days",
  },
  {
    value: ETokenCostPeriod.LAST_30_DAYS,
    labelKey: "tokenCostUsage.period.last30Days",
  },
  {
    value: ETokenCostPeriod.CURRENT_MONTH,
    labelKey: "tokenCostUsage.period.currentMonth",
  },
  {
    value: ETokenCostPeriod.ALL_TIME,
    labelKey: "tokenCostUsage.period.allTime",
  },
] as const;

export const DEFAULT_TOKEN_COST_PERIOD = ETokenCostPeriod.LAST_30_DAYS;

export const TOKEN_COST_TREND_METRIC_OPTIONS = [
  {
    value: ETokenUsageTrendMetric.TOKENS,
    labelKey: "tokenCostUsage.trends.metric.tokens",
  },
  {
    value: ETokenUsageTrendMetric.COST,
    labelKey: "tokenCostUsage.trends.metric.cost",
  },
] as const;

export const DEFAULT_TOKEN_COST_TREND_METRIC = ETokenUsageTrendMetric.TOKENS;

// NOTE: NSAI-7024 — the usage-breakdown dimensions, in the order both apps
// offer them. Assistant first: it is the question that prompted the panel.
export const TOKEN_USAGE_BREAKDOWN_DIMENSION_OPTIONS = [
  {
    value: ETokenUsageBreakdownDimension.ASSISTANT,
    labelKey: "tokenCostUsage.breakdown.dimension.assistant",
  },
  {
    value: ETokenUsageBreakdownDimension.AGENT,
    labelKey: "tokenCostUsage.breakdown.dimension.agent",
  },
  {
    value: ETokenUsageBreakdownDimension.SOURCE,
    labelKey: "tokenCostUsage.breakdown.dimension.source",
  },
] as const;

export const DEFAULT_TOKEN_USAGE_BREAKDOWN_DIMENSION =
  ETokenUsageBreakdownDimension.ASSISTANT;

// NOTE: NSAI-5172 — the measure names, reused by the chart's accessible label so
// a screen-reader user is told which of the two the chart is currently plotting.
// Points at the same keys the toggle renders, so the spoken name and the visible
// control can never say different things.
export const TOKEN_COST_TREND_METRIC_LABEL_KEYS = {
  [ETokenUsageTrendMetric.TOKENS]: "tokenCostUsage.trends.metric.tokens",
  [ETokenUsageTrendMetric.COST]: "tokenCostUsage.trends.metric.cost",
} as const;

// NOTE: i18n keys for the "partial latest bucket" note, keyed by the granularity
// the server actually used.
export const TOKEN_COST_TREND_UNIT_LABEL_KEYS = {
  [ETokenUsageTrendGranularity.DAY]: "tokenCostUsage.trends.unit.day",
  [ETokenUsageTrendGranularity.WEEK]: "tokenCostUsage.trends.unit.week",
  [ETokenUsageTrendGranularity.MONTH]: "tokenCostUsage.trends.unit.month",
} as const;

// NOTE: How many model series the chart colours from the categorical ramp before
// the aggregate remainder takes the neutral. Matches the server's series cap, so
// raising it means raising it there first.
export const TOKEN_COST_TREND_SERIES_COUNT = 4;

// NOTE: NSAI-5153 — the delimiter joining (provider, model_name) into a row key
// for usage with no catalog row. "~" rather than "-" because a hyphen is not a
// delimiter here: it occurs inside real provider ids ("z-ai") and inside almost
// every model name, so joining on it lets two different pairs flatten to the
// same key — the collision this row key exists to prevent. "~" is the character
// `models.key` already uses to compose region~provider~name~hosting for exactly
// this reason, so the convention is the repo's, not this file's.
export const ROW_KEY_SEPARATOR = "~";

// NOTE: NSAI-5207 — the single em dash both the cost figures and the token
// figures render for "unknown". Kept here rather than inline so the two never
// drift into different glyphs, which would read as two different states.
export const UNKNOWN_VALUE_PLACEHOLDER = "—";

// NOTE: NSAI-5214 — the currency precision the whole screen agrees on. Spend
// figures render at the floor — two decimals, the cent (NSAI-7005). The
// maximum is the server's rounding: rates (cost per 1K tokens) and a sub-cent
// trend axis may widen up to it, never beyond, so they never claim more
// precision than the data behind them.
export const TOKEN_COST_MIN_FRACTION_DIGITS = 2;
export const TOKEN_COST_MAX_FRACTION_DIGITS = 6;

// NOTE: NSAI-7005 — one cent: the smallest spend the screen prints. A positive
// spend below it renders as "<$0.01".
export const TOKEN_COST_SMALLEST_SPEND = 0.01;

// NOTE: NSAI-5612 — usage limits are USD-only in v1 (the server's
// TOKEN_COST_CURRENCY), and unlike the cost REPORT, which is handed a currency
// by the API, a configured cap carries no currency of its own. Named rather
// than inlined so adding a second currency has one place to start.
export const TOKEN_COST_CURRENCY = "USD";

// NOTE: A budget is a round figure an owner typed, so it renders at cents. The
// six-decimal maximum above serves per-unit rates (Avg. cost / 1K tokens) and a
// sub-cent trend axis, and would put "$4.203912 of $5.00" beside a progress bar.
export const USAGE_LIMIT_COST_FRACTION_DIGITS = 2;

// NOTE: NSAI-7080 — the Activity list reads a figure of a dollar or more to the
// cent: this many decimals, rounded on the decimal value (see
// roundToCents), not on its binary float.
export const TOKEN_USAGE_CENT_DECIMALS = 2;

// NOTE: NSAI-7080 — the hint under the summary's Total cost, one per state the
// figure can be in: complete, a "+" lower bound, or "—" (nothing costed).
export const TOKEN_USAGE_SUMMARY_COST_HINT_KEYS = {
  complete: "tokenUsage.summary.total_cost_hint",
  partial: "tokenUsage.summary.total_cost_partial_hint",
  unknown: "tokenUsage.summary.total_cost_unknown_hint",
} as const;

// NOTE: NSAI-6621 — a video model's catalog price is USD per second scaled to
// "per million seconds", so the shared cost formula prices it unchanged; this
// is the factor that turns it back into a per-second price for display.
export const TOKEN_USAGE_UNITS_PER_MILLION = 1_000_000;

// NOTE: NSAI-5249 — the rules the figure-notes affordance can state, paired with
// the test id each carries, so a spec asserting a rule is on screen asserts the
// same rule on either platform.
export const TOKEN_COST_FIGURE_NOTE_KEYS = {
  subset: {
    i18nKey: "tokenCostUsage.cachedSubsetLegend",
    testId: "cached-subset-legend",
  },
  unpriced: {
    i18nKey: "tokenCostUsage.unpricedLegend",
    testId: "unpriced-legend",
  },
  // NOTE: NSAI-5210 — the third reason a cost figure is blank, and the only one
  // where the money is KNOWN. Kept separate from `unpriced` so the two never
  // caption the same dash with opposite explanations (#4933).
  chargedAtInputRate: {
    i18nKey: "tokenCostUsage.chargedAtInputRateLegend",
    testId: "charged-at-input-rate-legend",
  },
  unreportedUsage: {
    i18nKey: "tokenCostUsage.unreportedUsageLegend",
    testId: "unreported-usage-legend",
  },
  // NOTE: NSAI-6266 — the one rule here that is about a DIFFERENT screen. This
  // report counts every usage type it can price; the enforced budget on Usage
  // Limits counts a narrower set, so the two report different money for the
  // same period — 0.655% apart on a title-heavy workload, growing with usage.
  // Deliberately does not name the excluded types: Usage Limits names them from
  // the API, and a second hand-maintained copy of that list is exactly how a
  // disclosure ends up describing a rule the enforcer no longer applies.
  enforcedBudgetScope: {
    i18nKey: "tokenCostUsage.enforcedBudgetScopeLegend",
    testId: "enforced-budget-scope-legend",
  },
} as const;

// NOTE: Derived from the constant rather than declared in `types/`, on purpose:
// widening `i18nKey` to `string` would defeat the typed `t()` in both apps, and
// only a type derived from the `as const` object above keeps the literals.
export type TTokenCostFigureNote =
  (typeof TOKEN_COST_FIGURE_NOTE_KEYS)[keyof typeof TOKEN_COST_FIGURE_NOTE_KEYS];

// NOTE: NSAI-5167 — the page-size vocabulary for the per-model breakdown. Shared
// rather than per-app so a reader meets the same page sizes on the web table and
// the mobile card list (NSAI-5001); it deliberately mirrors the Token Usage
// activity list on the sibling settings tab, so the two tabs behave alike.
export const TOKEN_COST_ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50];
export const DEFAULT_TOKEN_COST_ROWS_PER_PAGE = 10;

// NOTE: The first page. Named because it is used twice: as the initial state and
// as the value a period change resets to.
export const FIRST_TOKEN_COST_PAGE = 1;
